import { ActionTree } from 'vuex'
import { RootState } from '../../types';
import { SubscriptionState, MessageListSubscription } from './types';

export const subscriptionActions: ActionTree<SubscriptionState, RootState> = {
  async FETCH_MESSAGE_LIST_SUBSCRIPTIONS({ commit, state }, code: string) {
    try {
      commit('RESET_FETCH_MESSAGE_LIST_SUBSCRIPTIONS_ERROR');
      commit('SET_IS_FETCHING_MESSAGE_LIST_SUBSCRIPTIONS', true);

      const response = await this.$axios.get('/subscription-uri', {
        params: {
          $filter: `subscriptionCode=${code}`,
          ...state.sourceQueryParams,
        }
      });
      const subscription = response.data;

      if (response.headers['x-auth-token']) {
        window.sessionStorage.setItem('_ar_fan_auth_token_', response.headers['x-auth-token']);
      }

      commit('SET_FAN_OID', subscription.fanOid);
      commit('SET_CURRENT_MESSAGE_LIST_SUBSCRIPTIONS', subscription.messageLists);

      return true;
    } catch (e) {
      window.sessionStorage.removeItem('_ar_fan_auth_token_');
      const statusCode = e.response.status;
      if (statusCode === 404) {
        commit('SET_FETCH_MESSAGE_LIST_SUBSCRIPTIONS_ERROR', {
          title: "Error",
          description: "No subscriptions found"
        });
      } else if (statusCode === 401) {
        commit('SET_FETCH_MESSAGE_LIST_SUBSCRIPTIONS_ERROR', {
          title: "Expired",
          description: "Your subscription code has expired. You will need to wait for a new link when you're messaged again",
        });
      } else {
        commit('SET_FETCH_MESSAGE_LIST_SUBSCRIPTIONS_ERROR', {
          title: "Error",
          description: "Failed to fetch subscriptions"
        });
      }

      return false;
    } finally {
      commit('SET_IS_FETCHING_MESSAGE_LIST_SUBSCRIPTIONS', false);
    }
  },
  async CREATE_MESSAGE_LIST_SUBSCRIPTION({ state }, {
    messageListOid,
    subscription,
    notification = true,
  }) {
    const { fanOid, sourceTaskOid } = state;
    if (!fanOid) {
      return false;
    }
    const payload: any = {
      ...subscription,
      messageListOid,
      fanOid,
    };
    if (sourceTaskOid) {
      payload.taskOid = sourceTaskOid;
    }
    try {
      await this.$axios.post(`/fan/${fanOid}/subscription`, payload);
      if (notification) {
        // @ts-ignore
        this._vm.$arNotification.push({ type: 'success', message: 'Update successful' });
      }
      return true;
    } catch (e) {
      if (notification) {
        // @ts-ignore
        this._vm.$arNotification.push({ type: 'error', message: 'Failed to update' });
      }
      return false;
    }
  },
  async UPDATE_MESSAGE_LIST_SUBSCRIPTION({ state, commit }, {
    subscriptionOid,
    subscription,
    notification = true,
  }) {
    const { fanOid, sourceTaskOid } = state;
    if (!fanOid) {
      return false;
    }
    const payload: any = {
      ...subscription,
    };
    if (sourceTaskOid) {
      payload.taskOid = sourceTaskOid;
    }
    try {
      commit('SET_IS_UPDATING_MESSAGE_LIST_SUBSCRIPTION', true);

      await this.$axios.patch(`/fan/${fanOid}/subscription/${subscriptionOid}`, payload);
      if (notification) {
        // @ts-ignore
        this._vm.$arNotification.push({ type: 'success', message: 'Successfully updated subscription' });
      }
      return true;
    } catch (e) {
      if (notification) {
        // @ts-ignore
        this._vm.$arNotification.push({ type: 'error', message: 'Failed to update subscription' });
      }
      return false;
    } finally {
      commit('SET_IS_UPDATING_MESSAGE_LIST_SUBSCRIPTION', false);
    }
  },
  async UPDATE_MULTIPLE_MESSAGE_LIST_SUBSCRIPTIONS({ dispatch, state, commit }, messageListSubscriptions: MessageListSubscription[]) {
    const { fanOid, sourceTaskOid } = state;
    if (!fanOid) {
      return;
    }

    commit('SET_IS_UPDATING_MULTIPLE_MESSAGE_LIST_SUBSCRIPTIONS', true);

    const updateRequests = messageListSubscriptions.map(async (messageList: any) => {
      const subscription = messageList.subscription;
  
      if (subscription.oid) {
        const succeed = await dispatch('UPDATE_MESSAGE_LIST_SUBSCRIPTION', {
          subscriptionOid: subscription.oid,
          subscription: {
            sms: messageList.subscription.sms,
            email: messageList.subscription.email,
            facebookMessenger: messageList.subscription.facebookMessenger,
          },
          notification: false,
        });
        return succeed;
      } else {
        const succeed = await dispatch('CREATE_MESSAGE_LIST_SUBSCRIPTION', {
          messageListOid: messageList.oid,
          subscription: {
            sms: messageList.subscription.sms,
            email: messageList.subscription.email,
            facebookMessenger: messageList.subscription.facebookMessenger,
          },
        });
        return succeed;
      }
    });
  
    const results: boolean[] = await Promise.all(updateRequests);

    if (results.every(s => s)) {
      // @ts-ignore
      this._vm.$arNotification.push({ type: 'success', message: 'Successfully updated your subscriptions' });
    } else {
      const numSuccessful = results.reduce(
        (acca, update) => acca + (update ? 1 : 0), 0
      );
      // @ts-ignore
      this._vm.$arNotification.push({ type: 'error', message: `Only ${numSuccessful} out of ${results.length} updates were made.` });
    }

    commit('SET_IS_UPDATING_MULTIPLE_MESSAGE_LIST_SUBSCRIPTIONS', false);
  },
  async UNSUB_GLOBALLY({ dispatch, state, commit }) {
    const { fanOid, sourceMessageListOid } = state;
    if (!fanOid) {
      return;
    }

    commit('SET_IS_UPDATING_MULTIPLE_MESSAGE_LIST_SUBSCRIPTIONS', true);

    try {
      await this.$axios.post(`/fan/${fanOid}/unsub-globally`, {messageListOid: sourceMessageListOid});
      // @ts-ignore
      this._vm.$arNotification.push({ type: 'success', message: 'Successfully updated your subscriptions' });
    } catch (e) {
      // @ts-ignore
      this._vm.$arNotification.push({ type: 'error', message: 'Failed to update' });
    }

    commit('SET_IS_UPDATING_MULTIPLE_MESSAGE_LIST_SUBSCRIPTIONS', false);
  },
};
